import React, { useRef, useEffect, useState } from 'react';
import { Box, Text, Group } from '@mantine/core';
import classes from './NumberLine.module.css';

interface NumberLineProps {
    value: number;
    maxWidth?: number;
    height?: number;
    label?: string;
    leftColor?: string;
    rightColor?: string;
    leanLeftLabel?: string;
    leanRightLabel?: string;
    categoryLabel?: string;
}

export const NumberLine: React.FC<NumberLineProps> = ({
    value,
    maxWidth = 500,
    height = 80,
    label = "AB", // Default label if none provided
    leftColor = '#ff6b6b', // Default left color (red)
    rightColor = '#51cf66',  // Default right color (green)
    leanLeftLabel = "Left",  // Default lean left label
    leanRightLabel = "Right", // Default lean right label
    categoryLabel = "Lean" // Default category label
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(300);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // Ensure value is between -1 and 1
    const clampedValue = Math.max(-1, Math.min(1, value));

    // Split the label into two characters
    const [leftLabel, rightLabel] = label.length === 2
        ? [label[0], label[1]]
        : ["A", "B"]; // Fallback if label doesn't have exactly 2 chars

    // Calculate the position of the dot based on the value
    const position = ((clampedValue + 1) / 2) * containerWidth;

    // Calculate the center position (0 value)
    const centerPosition = containerWidth / 2;

    // Calculate the filled line styles
    const lineWidth = Math.abs(position - centerPosition);
    const lineStart = clampedValue >= 0 ? centerPosition : position;

    // Calculate the percentage (absolute value)
    const percentage = Math.abs(clampedValue * 100).toFixed(0);

    // Determine dot and line color based on value
    const dotColor = value < 0 ? leftColor : value > 0 ? rightColor : '#868e96';

    // Determine which lean label to use based on value
    const leanLabel = value < 0 ? leanLeftLabel : value > 0 ? leanRightLabel : "";

    return (
        <Box h={height}>
            <Group mb={5} justify="center" gap={20}>
                <Text fw={300} size="1rem">
                    {categoryLabel}
                </Text>
                <Text fw={500} size="1.2rem">
                    {`${percentage}%`}
                    <Text fw={400} component='span' size="1rem" ml={8}>
                        {leanLabel}
                    </Text>
                </Text>

            </Group>
            <div 
                ref={containerRef}
                className={classes.numberLine} 
                style={{ 
                    width: '100%',
                    maxWidth: maxWidth 
                }}
            >
                {/* Filled line from 0 to the point */}
                <div
                    className={classes.filledLine}
                    style={{
                        left: lineStart,
                        width: lineWidth,
                        backgroundColor: dotColor
                    }}
                />

                {/* Left mark (-1) */}
                <div className={`${classes.mark} ${classes.markLeft}`}>
                    <span
                        className={`${classes.markLabel} ${classes.markLabelLeft}`}
                        style={{ color: leftColor }}
                    >
                        {leftLabel}
                    </span>
                </div>

                {/* Center mark (0) */}
                <div className={`${classes.mark} ${classes.markCenter}`}>
                    <span className={`${classes.markLabel} ${classes.markLabelCenter}`}>
                        ^
                    </span>
                </div>

                {/* Right mark (1) */}
                <div className={`${classes.mark} ${classes.markRight}`}>
                    <span
                        className={`${classes.markLabel} ${classes.markLabelRight}`}
                        style={{ color: rightColor }}
                    >
                        {rightLabel}
                    </span>
                </div>

                {/* The value dot */}
                <div
                    className={classes.valueDot}
                    style={{
                        left: position,
                        backgroundColor: dotColor
                    }}
                />
            </div>
        </Box>
    );
};

export default NumberLine; 